import { Link } from '@tanstack/react-router';
import type { InferResponseType } from 'hono/client';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import { CATEGORY_DISPLAY_ORDER } from '~/components/features/interviews/interviews.types';
import { Badge } from '~/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '~/components/ui/table';
import type { api } from '~/lib/api';
import { cn } from '~/lib/utils';

export type ApplicationResult = InferResponseType<
  (typeof api.api.org)[':orgId']['department'][':deptId']['jobs'][':jobId']['results']['$get']
>['results'][number];

export interface Stage {
  id: string;
  name: string;
}

export interface Question {
  id: string;
  category: string;
  text: string;
}

interface ResultsTableProps {
  results: ApplicationResult[];
  questions: Question[];
  orgSlug: string;
  deptSlug: string;
  jobId: string;
}

const CATEGORY_LABEL_CLASS: Record<string, string> = {
  eligibility: 'text-[var(--color-section-blue)]',
  nice_to_have: 'text-[var(--color-section-teal)]',
  job_preview: 'text-[var(--color-section-green)]',
  assessment: 'text-[var(--color-section-orange)]',
  information: 'text-[var(--color-section-purple)]'
};

function getOutcomeVariant(outcome: string): 'secondary' | 'destructive' {
  return outcome === 'pass' ? 'secondary' : 'destructive';
}

function getStatusVariant(status: string | null): 'secondary' | 'destructive' | 'outline' {
  if (status === 'completed') return 'secondary';
  if (status === 'failed') return 'destructive';
  if (status === 'pending' || status === 'processing') return 'secondary';
  return 'outline';
}

export function ResultsTable({
  results,
  questions,
  orgSlug,
  deptSlug,
  jobId
}: ResultsTableProps): React.ReactNode {
  const { t } = useTranslation();

  const sortedQuestions = [...questions].sort(
    (a, b) =>
      CATEGORY_DISPLAY_ORDER.indexOf(a.category as (typeof CATEGORY_DISPLAY_ORDER)[number]) -
      CATEGORY_DISPLAY_ORDER.indexOf(b.category as (typeof CATEGORY_DISPLAY_ORDER)[number])
  );

  if (results.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20">
        <p className="text-base font-semibold text-foreground">{t('results.emptyTitle')}</p>
        <p className="max-w-sm text-center text-sm text-muted-foreground">
          {t('results.emptyDescription')}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border">
      <Table className="min-w-max">
        <TableHeader>
          <TableRow>
            <TableHead className="sticky left-0 z-10 w-52 bg-background px-4 py-3">
              {t('results.columns.candidate')}
            </TableHead>
            {sortedQuestions.map((q) => (
              <TableHead key={q.id} className="w-56 align-top whitespace-normal px-4 py-3">
                <p
                  className={cn(
                    'mb-1 text-xs font-semibold uppercase tracking-wide',
                    CATEGORY_LABEL_CLASS[q.category]
                  )}
                >
                  {t(
                    `results.questionSections.${q.category}` as 'results.questionSections.eligibility'
                  )}
                </p>
                <p className="text-sm font-medium text-foreground">{q.text}</p>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {results.map((r) => {
            const scoreMap = new Map(r.questionScores.map((qs) => [qs.questionId, qs]));
            return (
              <TableRow key={r.applicationId}>
                <TableCell className="sticky left-0 z-10 w-52 bg-background align-top px-4 py-3">
                  <div className="flex flex-col gap-2">
                    <div>
                      <Link
                        to="/org/$orgSlug/department/$deptSlug/jobs/$jobId/$applicationId"
                        params={{ orgSlug, deptSlug, jobId, applicationId: r.applicationId }}
                        className="text-sm font-semibold hover:underline"
                      >
                        {r.candidateName}
                      </Link>
                      <p className="text-xs text-muted-foreground">{r.candidateEmail}</p>
                    </div>
                    {r.overallScore !== null && (
                      <p className="text-xl font-bold leading-none">
                        {r.overallScore}
                        <span className="ml-0.5 text-xs font-normal text-muted-foreground">
                          / 10
                        </span>
                      </p>
                    )}
                    <div className="flex flex-wrap gap-1">
                      {r.outcome && (
                        <Badge
                          variant={getOutcomeVariant(r.outcome)}
                          className={cn(
                            r.outcome === 'pass' &&
                              'border-green-200 bg-green-100 text-green-700 hover:bg-green-100'
                          )}
                        >
                          {t(`scoring.outcomes.${r.outcome}` as 'scoring.outcomes.pass')}
                        </Badge>
                      )}
                      <Badge
                        variant={getStatusVariant(r.scoreStatus)}
                        className={cn(!r.scoreStatus && 'text-muted-foreground')}
                      >
                        {r.scoreStatus
                          ? t(
                              `results.statusLabels.${r.scoreStatus}` as 'results.statusLabels.pending'
                            )
                          : t('results.statusLabels.no_interview')}
                      </Badge>
                    </div>
                  </div>
                </TableCell>
                {sortedQuestions.map((q) => {
                  const qs = scoreMap.get(q.id);
                  return (
                    <TableCell key={q.id} className="w-56 align-top whitespace-normal px-4 py-3">
                      {qs ? (
                        <div className="flex flex-col gap-1.5">
                          {q.category === 'assessment' && qs.score !== null && (
                            <p className="text-base font-bold leading-none">
                              {qs.score}
                              <span className="ml-0.5 text-xs font-normal text-muted-foreground">
                                / 5
                              </span>
                            </p>
                          )}
                          {q.category !== 'assessment' &&
                            q.category !== 'information' &&
                            qs.verdict && (
                              <Badge
                                variant={qs.verdict === 'fail' ? 'destructive' : 'secondary'}
                                className={cn(
                                  'w-fit',
                                  qs.verdict === 'pass' &&
                                    'border-green-200 bg-green-100 text-green-700 hover:bg-green-100'
                                )}
                              >
                                {qs.verdict}
                              </Badge>
                            )}
                          {qs.reasoning && (
                            <p className="text-sm text-muted-foreground">{qs.reasoning}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground/50">—</span>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
